import { cva } from "class-variance-authority";
import {
  CircleAlert,
  CircleCheck,
  Info,
  type LucideIcon,
  OctagonAlert,
  TriangleAlert,
} from "lucide-react";
import * as React from "react";

import { cn } from "@/utils/shadcn-ui";

/**
 * shadcn's `Alert`, exported as `Callout`: "alert" already names a product
 * concept here (patient alerts) and one of the status tones, so
 * `<Alert severity="alert">` would read badly. Upstream is shadcn's
 * `alert.tsx`.
 *
 * Each severity maps onto the status tokens in `css/main.css` and brings its
 * own icon, so callers pass only the content.
 */
const calloutVariants = cva(
  "grid w-full grid-cols-[calc(var(--spacing)*4)_1fr] items-start gap-x-3 gap-y-0.5 rounded-lg border px-4 py-3 text-sm [&>svg]:size-4 [&>svg]:translate-y-0.5 [&>svg]:text-current",
  {
    variants: {
      severity: {
        info: "border-info-border bg-info-soft text-info-foreground",
        success:
          "border-success-border bg-success-soft text-success-foreground",
        warning:
          "border-warning-border bg-warning-soft text-warning-foreground",
        alert: "border-alert-border bg-alert-soft text-alert-foreground",
        danger: "border-danger-border bg-danger-soft text-danger-foreground",
      } satisfies Record<CalloutSeverity, string>,
    },
  },
);

/**
 * `role="note"` by default, for static guidance that is there from the start.
 * Messages that appear in response to something should pass `role="alert"`, so
 * a screen reader announces them immediately.
 */
function Callout({
  className,
  severity = "info",
  children,
  ...props
}: React.ComponentProps<"div"> & { severity?: CalloutSeverity }) {
  const Icon = severityIcons[severity];
  return (
    <div
      data-slot="callout"
      role="note"
      className={cn(calloutVariants({ severity }), className)}
      {...props}
    >
      <Icon />
      {children}
    </div>
  );
}

function CalloutDescription({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="callout-description"
      className={cn(
        "col-start-2 grid justify-items-start gap-1 text-sm [&_p]:leading-relaxed",
        className,
      )}
      {...props}
    />
  );
}

const severityIcons = {
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  alert: CircleAlert,
  danger: OctagonAlert,
} satisfies Record<string, LucideIcon>;

type CalloutSeverity = keyof typeof severityIcons;

export { Callout, CalloutDescription };
